import React from 'react';

import { useMutation } from '@tanstack/react-query';
import { StraightArrowUpIcon } from '@vlabs/icons';
import { Control, useModal } from '@vlabs/uikit';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { linkFaces, getGetFacesQueryKey } from '@vlabs/api/luna-api/client';
import { queryClient } from '@vlabs/api/queryClient';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { MoveListForm } from './forms';

const displayName = 'MoveFacesToListButton';
const testid = buildTestid(displayName);
interface MoveFacesToListButtonProps extends WithDataTestid {
  faceIds: string[];
  onMoveFacesToList?: VoidFunction;
}

export function MoveFacesToListButton({
  faceIds,
  'data-testid': dataTestId = undefined,
}: MoveFacesToListButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const modal = useModal({ size: 'small' });
  const { listId } = useParams<{ listId: string }>();

  const moveFacesToListMutation = useMutation({
    mutationFn: async (attachListId: string) => {
      try {
        await linkFaces(attachListId, {
          action: 'attach',
          face_ids: faceIds,
        });
      } catch (e) {
        toast.error(t('features:move_faces_to_list.toast.error.attach'));
        throw e;
      }

      try {
        await linkFaces(listId, {
          action: 'detach',
          face_ids: faceIds,
        });
      } catch (e) {
        toast.error(t('features:move_faces_to_list.toast.error.detach'));
        throw e;
      }

      toast.success(t('features:move_faces_to_list.toast.success'));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getGetFacesQueryKey(),
      });
      modal.close();
    },
  });

  return (
    <React.Fragment>
      {modal.wrap(<MoveListForm
        onCancel={modal.close}
        onSubmit={moveFacesToListMutation.mutateAsync}
      />)}
      <Control.Button
        icon={<StraightArrowUpIcon />}
        kind="primary"
        onClick={modal.open}
        size="small"
        variant="flat-inverted"
        {...testid(dataTestId)}
      >
        {t('features:move_faces_to_list.buttons.move')}
      </Control.Button>
    </React.Fragment>
  );
}
